"use client";

import { Loader2, Play, Share2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import type { Persona } from "../context/AnalyzeFileContext";
import { SHARE_PERMISSIONS_TOOLTIP } from "../analyzeConstants";

type AnalyzeRunBarProps = {
  focusedPersona: Persona | null;
  isGenerating: boolean;
  isLocked: boolean;
  hasInsightsForPersona: boolean;
  onRun: () => void;
  showShareNudge?: boolean;
  onShareNudgeClick?: () => void;
  embeddedInShell?: boolean;
};

export const AnalyzeRunBar = ({
  focusedPersona,
  isGenerating,
  isLocked,
  hasInsightsForPersona,
  onRun,
  showShareNudge,
  onShareNudgeClick,
  embeddedInShell,
}: AnalyzeRunBarProps) => {
  if (!focusedPersona) {
    return null;
  }

  return (
    <div
      className={cn(
        "shrink-0 border-t py-2",
        embeddedInShell
          ? "border-border/40 bg-transparent px-0"
          : "border-border bg-card px-3",
      )}
    >
      <div className="flex items-center gap-2">
        <Button
          type="button"
          className="flex-1 min-w-0 gap-2 h-8 text-sm"
          onClick={onRun}
          disabled={isLocked || isGenerating}
        >
          {isGenerating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
              Analyzing…
            </>
          ) : (
            <>
              <Play className="w-4 h-4 fill-current shrink-0" />
              <span className="truncate">
                {hasInsightsForPersona ? `Run again — ${focusedPersona.name}` : `Run — ${focusedPersona.name}`}
              </span>
            </>
          )}
        </Button>

        <AnimatePresence initial={false}>
          {showShareNudge && onShareNudgeClick && (
            <motion.div
              key="share-nudge"
              initial={{ opacity: 0, scale: 0.88, x: 6 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.88, x: 6 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="shrink-0"
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 shrink-0 gap-1 px-2.5 text-xs font-normal text-muted-foreground border-border/80 bg-background hover:bg-muted/50 hover:text-foreground"
                onClick={onShareNudgeClick}
                title={SHARE_PERMISSIONS_TOOLTIP}
                aria-label="Open Share and Permissions"
              >
                <Share2 className="w-3.5 h-3.5 shrink-0" />
                Share
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
